/**
 * Ellipse entity → `RenderableEntity` adapter.
 *
 * Reads `Center X / Y`, `Major axis X / Y` (vector relative to centre),
 * `Ratio` (minor/major), `Start parameter` / `End parameter` (radians).
 * Matches the keys used by `renderEllipse` in
 * apps/web/src/app/scene/renderSimpleEntities.tsx.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { readNumber, readPositiveNumber } from "../../scene/renderHelpers";
import type { RenderableEntity, Vec3 } from "../types";

import {
  buildRenderableShell,
  readCenterRaw,
  type AdapterContext,
} from "./adapterContext";
import { buildEllipseSceneGeometry } from "./sceneGeometryBuilders";

export function ellipseToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity {
  const centerRaw = readCenterRaw(entity.properties);
  const majorAxis: Vec3 = {
    x: readNumber(entity.properties, "Major axis X"),
    y: readNumber(entity.properties, "Major axis Y"),
    z: readNumber(entity.properties, "Major axis Z"),
  };
  const ratio = readPositiveNumber(entity.properties, "Ratio", 1);
  const startParam = readNumber(entity.properties, "Start parameter");
  const endParam = readNumber(
    entity.properties,
    "End parameter",
    Math.PI * 2,
  );
  // SL-15 F-C — OCS extrusion normal (DXF 210/220/230) from the bag; absent keys
  // default to the (0,0,1) WCS normal (planar ellipse → byte-identical 2D render).
  const extrusion: Vec3 = {
    x: readNumber(entity.properties, "Extrusion X"),
    y: readNumber(entity.properties, "Extrusion Y"),
    z: readNumber(entity.properties, "Extrusion Z", 1),
  };

  const { geometry, bounds } = buildEllipseSceneGeometry(
    centerRaw,
    majorAxis,
    ratio,
    startParam,
    endParam,
    extrusion,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
